import { observer } from 'mobx-react-lite'
import { useEffect } from 'react'
import { WarehousesController } from './warehouses.controller'
import i from '../../../translations/i'
import { Button, Space, Table, Row, Col, Input, Select, InputNumber, Upload, notification, Switch, Divider } from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined, HomeOutlined } from '@ant-design/icons'
import { Titles } from '../../../layouts/Titles'
import { useParams } from 'react-router-dom'
import { ParamTypes } from '../../../helpers/types.interfaces'
import { storeLocal } from '../../../helpers/stores'
import cities from '../cities/cities.controller'
import controller from "../cities/cities.controller";
import { SectionBadge, PlainField, PlainToggle } from '../../../components/SectionCard'

const Warehouses = new WarehousesController()

export const Storages = observer(() => {

    useEffect(() => {
        Warehouses.get()
    }, [])

    const buttons = [
        { title: i.t('def:actions.create'), href: `/settings/warehouses/0`, type: 'primary' }
    ]

    const columns = [
        { title: 'ID', dataIndex: 'id', key: 'id' },
        {
            title: i.t('settings:cities.form.enabled'),
            dataIndex: 'enabled',
            key: 'enabled',
            render: (text: any, record: any) => (
              <>{text === 0 ? i.t('def:no') : i.t('def:yes')}</>
            )
        },
        { title: i.t('def:name'), dataIndex: 'name', key: 'name' },
        {
            key: 'action',
            render: (text: any, record: any) => (
                <Space size="middle">
                    <Button
                        type="dashed"
                        shape="circle"
                        icon={<EditOutlined />}
                        href={`/settings/warehouses/${record.id}`}
                    />
                    <Button
                        type="dashed"
                        shape="circle"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => Warehouses.remove(record.id)}
                    />
                </Space>
            )
        }
    ]

    return(
        <>
            <Titles
                title={i.t('settings:storage.title')}
                buttons={buttons}
            />
            {storeLocal.get('pricing_plan') >= 2 &&
                <>
                    <Table
                        columns={columns}
                        dataSource={Warehouses.list}
                        pagination={{
                            position: ['bottomLeft'],
                            hideOnSinglePage: true,
                            current: Number(Warehouses.pagination.page),
                            pageSize: Warehouses.pagination.limit,
                            total: Warehouses.pagination.total,
                            showSizeChanger: false,
                            onChange: (e) => Warehouses.setPage(e)
                        }}
                    />
                </>
            }
        </>
    )
})

export const StorageElement = observer(() => {

    const { elementID } = useParams<ParamTypes>()

    useEffect(() => {
        cities.pagination.limit = 999
        cities.get()
        // @ts-ignore
        if(elementID > 0) Warehouses.getElement(elementID)
        Warehouses.getZones()
        Warehouses.getTimezones()
        Warehouses.getPlaces()
    }, [])

    const buttons = [
        { title: i.t('def:actions.back'), href: '/settings/warehouses', type: 'dashed' },
        { title: i.t('def:actions.save'), htmlType: `submit`, type: 'primary' }
    ]

    return(
        <>
            <form onSubmit={(e) => Warehouses.save(e)} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <Titles
                    title={i.t('settings:storage.titleDetail')}
                    buttons={buttons}
                />

                <SectionBadge
                    icon={<HomeOutlined />}
                    text={Warehouses.element.name || i.t('settings:storage.titleDetail')}
                    background="linear-gradient(135deg, var(--primary-color), #9186e0)"
                    toggle={
                        <Switch
                            checked={Warehouses.element.enabled}
                            onChange={(e) => Warehouses.onChange('enabled', e)}
                            checkedChildren={i.t('def:on')}
                            unCheckedChildren={i.t('def:off')}
                        />
                    }
                />

                <PlainToggle
                    label={i.t('settings:storage.showAvailability')}
                    checked={!!Warehouses.element.show_availability}
                    onChange={(e) => Warehouses.onChange('show_availability', e)}
                />

                <Divider dashed style={{ margin: 0 }} />

                <Row gutter={[16, 16]} style={{ width: '100%', margin: 0 }}>
                    <Col span={12} style={{ paddingLeft: 0 }}>
                        <PlainField label={i.t('def:name')}>
                            <Input
                                placeholder={i.t('def:name')}
                                value={Warehouses.element.name}
                                onChange={(e) => Warehouses.onChange('name', e.target.value)}
                            />
                        </PlainField>
                    </Col>
                    <Col span={12} style={{ paddingRight: 0 }}>
                        <PlainField label={i.t('def:id_crm')}>
                            <Input
                                placeholder={i.t('def:id_crm')}
                                value={Warehouses.element.id_crm}
                                onChange={(e) => Warehouses.onChange('id_crm', e.target.value)}
                            />
                        </PlainField>
                    </Col>
                </Row>

                <PlainField label={i.t('settings:storage.city')}>
                    <Select
                        mode="multiple"
                        value={Warehouses.element.city_ids ?? []}
                        style={{ width: '100%' }}
                        onChange={(e) => Warehouses.onChange('city_ids', e)}
                    >
                        {cities.list.map((el: any) => (
                            <Select.Option value={el.id} key={el.id}>
                                {el.name}
                            </Select.Option>
                        ))}
                    </Select>
                </PlainField>

                <PlainField label={i.t('settings:storage.timezone')}>
                    <Select
                        showSearch
                        allowClear
                        style={{ width: '100%' }}
                        value={Warehouses.element.timezone || undefined}
                        placeholder={i.t('settings:storage.timezoneInherit')}
                        onChange={(e) => Warehouses.onChange('timezone', e ?? null)}
                        options={Warehouses.timezones}
                        filterOption={(input, option) => `${option?.label ?? ''}`.toLowerCase().includes(input.toLowerCase())}
                    />
                    <div style={{ marginTop: 8, opacity: 0.65 }}>{i.t('settings:storage.timezoneHint')}</div>
                </PlainField>

                <Row gutter={[16, 16]} style={{ width: '100%', margin: 0 }}>
                    <Col span={12} style={{ paddingLeft: 0 }}>
                        <PlainField label={i.t('settings:storage.latitude')}>
                            <InputNumber
                                style={{ width: '100%' }}
                                controls={false}
                                placeholder={i.t('settings:storage.latitude')}
                                value={Warehouses.element.latitude}
                                onChange={(e) => Warehouses.onChange('latitude', e)}
                            />
                        </PlainField>
                    </Col>
                    <Col span={12} style={{ paddingRight: 0 }}>
                        <PlainField label={i.t('settings:storage.longitude')}>
                            <InputNumber
                                style={{ width: '100%' }}
                                controls={false}
                                placeholder={i.t('settings:storage.longitude')}
                                value={Warehouses.element.longitude}
                                onChange={(e) => Warehouses.onChange('longitude', e)}
                            />
                        </PlainField>
                    </Col>
                </Row>

                <Divider dashed style={{ margin: 0 }} />

                <PlainField label={i.t('settings:storage.zones')}>
                    <Select mode="multiple" value={Warehouses.element.zones} style={{ width: '100%' }} onChange={(e) => Warehouses.onChange('zone', e)}>
                        {Warehouses.zones.map((el: any, i) => <Select.Option value={el.id} key={i}>{el.name}</Select.Option>)}
                    </Select>
                </PlainField>

                <PlainField label={i.t('settings:storage.places')}>
                    <Select mode="multiple" value={Warehouses.element.places} style={{ width: '100%' }} onChange={(e) => Warehouses.onChange('place', e)}>
                        {Warehouses.places.map((el: any, i) => <Select.Option value={el.id} key={i}>{el.name}</Select.Option>)}
                    </Select>
                </PlainField>

                <Divider dashed style={{ margin: 0 }} />

                <PlainField label={i.t('settings:cities.form.image')}>
                    {Warehouses.element.picture?.id > 0 ? (
                        <Row gutter={[10, 10]}>
                            <Col xs={24}>
                                <img src={Warehouses.element.picture.base + Warehouses.element.picture.uri} alt='' style={{maxWidth: '250px'}}/>
                            </Col>
                            <Col xs={24}>
                                <Button
                                    onClick={e => Warehouses.removeImage(elementID)}
                                >
                                    {i.t('def:deletePicture')}
                                </Button>
                            </Col>
                        </Row>
                    ) : (
                        <Upload
                            beforeUpload={(info) => {
                                Warehouses.upload(info)
                                return false
                            }}
                            listType="picture-card"
                            className="photo-uploader"
                            maxCount={1}
                        >
                            <div>
                                <PlusOutlined />
                                <div style={{ marginTop: 8 }}>{i.t('def:image.upload')}</div>
                            </div>
                        </Upload>
                    )}
                </PlainField>
            </form>
        </>
    )
})